import { useCallback, useEffect, useState } from "react";
import * as React from "react";

import { SegmentedControl, SpacingToken } from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import { IAgent } from "store/entities/agents";
import { ILiveChatAgent, ISort } from "store/views/agents";
import { withVar } from "themes/helpers";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { SelectedAgentsList } from "../Agents";

import { HelpDeskAgentsList } from "./HelpDeskAgentsList";
import LiveChatAgentModal from "./LiveChatAgentModal";
import LiveChatAgentsList from "./LiveChatAgentsList";

interface IProps {
  sort: ISort;
  hasLiveChatIntegration: boolean;
  helpDeskAgentsCount: number;
  filteredHelpDeskAgents: IAgent[];
  liveChatAgentsCount: number;
  filteredLiveChatAgents: ILiveChatAgent[] | null;
  selectedAgent?: IAgent;
  selectedAgentsList: SelectedAgentsList;
  canImportLiveChatAgents: boolean;
  onFetchLiveChatAgents(): void;
  onSelectAgent(agentLogin: string): void;
  onDoubleClickAgent(agentLogin: string): void;
  onSort(field: string, desc: boolean): void;
  onChangeSelectedAgentsList(selectedAgentsList: SelectedAgentsList): void;
  onAddAgentClick(): void;
}

const AgentsList: React.FC<IProps> = ({
  sort,
  hasLiveChatIntegration,
  filteredHelpDeskAgents,
  helpDeskAgentsCount,
  liveChatAgentsCount,
  filteredLiveChatAgents,
  selectedAgent,
  selectedAgentsList,
  canImportLiveChatAgents,
  onSort,
  onSelectAgent,
  onDoubleClickAgent,
  onFetchLiveChatAgents,
  onChangeSelectedAgentsList,
  onAddAgentClick,
}) => {
  useEffect(() => {
    onFetchLiveChatAgents();
  }, [onFetchLiveChatAgents, hasLiveChatIntegration]);

  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [selectedLiveChatAgent, setSelectedLiveChatAgent] = useState<ILiveChatAgent | null>(null);

  useEffect(() => {
    if (filteredLiveChatAgents === null) {
      onChangeSelectedAgentsList(SelectedAgentsList.LiveChat);
    }
  }, [filteredLiveChatAgents, onChangeSelectedAgentsList]);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleButtonGroupClick = (id: string) => {
    const selectedAgentsList =
      parseInt(id) === SelectedAgentsList.HelpDesk ? SelectedAgentsList.LiveChat : SelectedAgentsList.HelpDesk;
    onChangeSelectedAgentsList(selectedAgentsList);
  };

  const handleLiveChatAgentImportClick = useCallback(
    (agentEmail: string) => {
      setSelectedLiveChatAgent(filteredLiveChatAgents?.find(agent => agent.email === agentEmail) ?? null);
      setModalVisible(true);
    },
    [filteredLiveChatAgents]
  );

  const handleModalClose = useCallback(() => {
    setModalVisible(false);
  }, []);

  return (
    <>
      {filteredLiveChatAgents && (
        <Block padding={withVar(SpacingToken.Spacing5)}>
          <SegmentedControl
            onButtonClick={handleButtonGroupClick}
            initialId={SelectedAgentsList.HelpDesk.toString()}
            buttons={[
              { id: SelectedAgentsList.HelpDesk.toString(), label: `Agents (${helpDeskAgentsCount})` },
              { id: SelectedAgentsList.LiveChat.toString(), label: `Agents from LiveChat (${liveChatAgentsCount})` },
            ]}
          />
        </Block>
      )}

      {selectedAgentsList === SelectedAgentsList.LiveChat && (
        <HelpDeskAgentsList
          sort={sort}
          agents={filteredHelpDeskAgents}
          selectedAgent={selectedAgent}
          onSort={onSort}
          onSelectAgent={onSelectAgent}
          onDoubleClickAgent={onDoubleClickAgent}
          onAddAgentClick={onAddAgentClick}
        />
      )}
      {filteredLiveChatAgents && selectedAgentsList === SelectedAgentsList.HelpDesk && (
        <LiveChatAgentsList
          sort={sort}
          agents={filteredLiveChatAgents}
          canImportLiveChatAgents={canImportLiveChatAgents}
          onSort={onSort}
          onLiveChatAgentImportClick={handleLiveChatAgentImportClick}
        />
      )}

      {modalVisible && <LiveChatAgentModal agent={selectedLiveChatAgent} onModalClose={handleModalClose} />}
    </>
  );
};

export default AgentsList;
